import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { MessageSquare, Send, X, Sparkles, MapPin, Star, Mic, MicOff, Keyboard, Volume2, VolumeX } from "lucide-react";
import { formatMoney } from "../lib/partnerPricing";
import { listingCoverImage } from "../lib/fileUrl";
import { useSpeechRecognition } from "../lib/useSpeechRecognition";
import { useSpeechSynthesis } from "../lib/useSpeechSynthesis";
import { AI_NAME } from "../lib/brand";

const SS_KEY = "zcars_chat_session";
const MODE_KEY = "zcars_chat_mode";

function getSessionId() {
  let id = sessionStorage.getItem(SS_KEY);
  if (!id) {
    id = `cs_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    sessionStorage.setItem(SS_KEY, id);
  }
  return id;
}

function ChatVehicleCards({ vehicles, search }) {
  if (!vehicles?.length) return null;
  const qs = new URLSearchParams();
  if (search?.city) qs.set("city", search.city);
  if (search?.start_date) qs.set("start", search.start_date);
  if (search?.end_date) qs.set("end", search.end_date);
  if (search?.vehicle_type) qs.set("vehicle_type", search.vehicle_type);
  const browseHref = `/browse${qs.toString() ? `?${qs}` : ""}`;

  return (
    <div className="mt-3 space-y-2" data-testid="ai-chat-vehicles">
      {vehicles.map(v => {
        const detailQs = new URLSearchParams(qs);
        const href = `/vehicles/${v.vehicle_id}${detailQs.toString() ? `?${detailQs}` : ""}`;
        const img = listingCoverImage(v.images, "https://images.unsplash.com/photo-1511919884226-fd3cad34687c?auto=format&fit=crop&w=400&q=80");
        return (
          <Link
            key={v.vehicle_id}
            to={href}
            className="flex gap-3 p-2 rounded-lg border border-[var(--zc-border)] hover:border-[var(--zc-accent)] transition bg-[var(--zc-bg-2)]"
            data-testid={`ai-chat-vehicle-${v.vehicle_id}`}
          >
            <img src={img} alt="" className="w-16 h-12 object-cover rounded-md shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold truncate">{v.year} {v.make} {v.model}</div>
              <div className="zc-meta flex items-center gap-2 mt-0.5">
                <span className="flex items-center gap-0.5"><MapPin size={9} />{v.location_city}</span>
                {v.avg_rating != null && (
                  <span className="flex items-center gap-0.5"><Star size={9} />{v.avg_rating}</span>
                )}
              </div>
              <div className="text-xs font-semibold mt-1 text-[var(--zc-accent)]">
                {formatMoney(v.daily_rate)}/day
              </div>
            </div>
          </Link>
        );
      })}
      <Link to={browseHref} className="text-xs text-[var(--zc-accent)] font-semibold hover:underline">
        View all matches →
      </Link>
    </div>
  );
}

const VOICE_GREETING =
  `Hi, I'm ${AI_NAME}. Tap the microphone and tell me a city and dates — for example, sports cars in Miami June tenth through fifteenth.`;

export default function AIChat() {
  const location = useLocation();
  const hideOnRoute =
    location.pathname.startsWith("/inspect/")
    || location.pathname.startsWith("/book/")
    || location.pathname.startsWith("/browse");

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(() => sessionStorage.getItem(MODE_KEY) || "text");
  const [speakReplies, setSpeakReplies] = useState(true);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: `Hi, I'm ${AI_NAME} — your Z-Cars concierge. Tell me a city and dates (e.g. "sports cars in Miami June 10–15") and I'll search the fleet for you.`,
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);
  const { supported: ttsSupported, speaking, speak, stop: stopSpeaking } = useSpeechSynthesis();

  const sendMessage = useCallback(async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || sending) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: trimmed }]);
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/chat`,
        { session_id: sessionId.current, message: trimmed },
        { withCredentials: true },
      );
      const reply = res.data.reply;
      setMessages(m => [
        ...m,
        {
          role: "assistant",
          content: reply,
          vehicles: res.data.vehicles || [],
          search: res.data.search || null,
        },
      ]);
      if (mode === "voice" && speakReplies && ttsSupported) {
        speak(reply);
      }
    } catch (e) {
      const errMsg = "Hmm, I had trouble reaching the engine. Try again in a moment.";
      setMessages(m => [...m, { role: "assistant", content: errMsg }]);
      if (mode === "voice" && speakReplies && ttsSupported) speak(errMsg);
    } finally {
      setSending(false);
    }
  }, [sending, mode, speakReplies, ttsSupported, speak]);

  const onVoiceFinal = useCallback((transcript) => {
    if (!transcript) return;
    setInput(transcript);
  }, []);

  const {
    supported: sttSupported,
    listening,
    interim,
    error: voiceError,
    toggle: toggleMic,
    stop: stopMic,
    clearError,
  } = useSpeechRecognition({ onFinal: onVoiceFinal });

  useEffect(() => {
    sessionStorage.setItem(MODE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, interim]);

  useEffect(() => {
    if (!open) {
      stopMic();
      stopSpeaking();
    }
  }, [open, stopMic, stopSpeaking]);

  useEffect(() => {
    if (mode !== "voice") {
      stopMic();
      stopSpeaking();
    }
  }, [mode, stopMic, stopSpeaking]);

  const switchMode = (next) => {
    if (next === mode) return;
    stopMic();
    stopSpeaking();
    clearError();
    setMode(next);
    if (next === "voice" && messages.length === 1 && speakReplies && ttsSupported) {
      setMessages([{ role: "assistant", content: VOICE_GREETING }]);
      speak(VOICE_GREETING);
    }
    if (next === "text" && messages.length === 1) {
      setMessages([{
        role: "assistant",
        content: `Hi, I'm ${AI_NAME} — your Z-Cars concierge. Tell me a city and dates (e.g. "sports cars in Miami June 10–15") and I'll search the fleet for you.`,
      }]);
    }
  };

  const send = () => sendMessage(input);

  const voiceReady = sttSupported;

  if (hideOnRoute) return null;

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-6 z-50 w-14 h-14 grid place-items-center zc-btn-primary"
        style={{ borderRadius: "9999px" }}
        data-testid="ai-chat-fab"
        aria-label="Open AI chat"
      >
        {open ? <X size={20} /> : <MessageSquare size={20} />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] max-h-[min(520px,calc(100dvh-6rem))] zc-card flex flex-col overflow-hidden"
          data-testid="ai-chat-panel"
        >
          <div className="px-4 py-3 border-b border-[var(--zc-border)] bg-[var(--zc-bg-3)]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-[var(--zc-accent)]" />
                <div className="text-sm font-semibold">{AI_NAME} · Concierge</div>
              </div>
              <span className={"dot " + (listening || speaking ? "live" : "pending")} />
            </div>
            <div className="flex gap-1 mt-2.5 p-0.5 rounded-lg bg-[var(--zc-bg-2)] border border-[var(--zc-border)]">
              <button
                type="button"
                onClick={() => switchMode("text")}
                className={"flex-1 h-7 text-caption font-semibold rounded-md flex items-center justify-center gap-1 transition " +
                  (mode === "text" ? "bg-white text-[var(--zc-text)] shadow-sm" : "text-[var(--zc-text-3)]")}
                data-testid="ai-chat-mode-text"
              >
                <Keyboard size={11} /> Text
              </button>
              <button
                type="button"
                onClick={() => switchMode("voice")}
                className={"flex-1 h-7 text-caption font-semibold rounded-md flex items-center justify-center gap-1 transition " +
                  (mode === "voice" ? "bg-white text-[var(--zc-text)] shadow-sm" : "text-[var(--zc-text-3)]")}
                data-testid="ai-chat-mode-voice"
              >
                <Mic size={11} /> Voice
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4 bg-white" data-testid="ai-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={"text-sm leading-relaxed " + (m.role === "user" ? "text-[var(--zc-text)]" : "text-[var(--zc-text-2)]")}>
                <div className="eyebrow mb-1.5" style={{ color: m.role === "user" ? "var(--zc-text)" : "var(--zc-accent)" }}>
                  {m.role === "user" ? "You" : AI_NAME}
                </div>
                <div className="whitespace-pre-wrap">{m.content}</div>
                {m.role === "assistant" && m.vehicles?.length > 0 && (
                  <ChatVehicleCards vehicles={m.vehicles} search={m.search} />
                )}
              </div>
            ))}
            {listening && interim && (
              <div className="text-xs text-[var(--zc-text-3)] italic" data-testid="ai-chat-interim">
                Hearing: {interim}…
              </div>
            )}
            {sending && <div className="text-xs text-[var(--zc-text-3)]">{AI_NAME} is searching…</div>}
            {speaking && <div className="text-xs text-[var(--zc-accent)]">{AI_NAME} is speaking…</div>}
          </div>

          {mode === "text" && (
            <div className="border-t border-[var(--zc-border)] p-3 flex items-center gap-2 bg-white">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") send(); }}
                placeholder="e.g. luxury cars in Miami June 10–15"
                className="zc-input flex-1 h-10 px-3 text-sm"
                data-testid="ai-chat-input"
                aria-label="Message"
              />
              <button onClick={send} disabled={sending} className="w-10 h-10 grid place-items-center zc-btn-primary" data-testid="ai-chat-send">
                <Send size={14} />
              </button>
            </div>
          )}

          {mode === "voice" && (
            <div className="border-t border-[var(--zc-border)] p-3 bg-white" data-testid="ai-chat-voice-bar">
              {!voiceReady ? (
                <p className="text-xs text-[var(--zc-text-3)] text-center py-2">
                  Voice search isn&apos;t supported in this browser. Use Chrome, Edge, or Safari — or switch to Text.
                </p>
              ) : (
                <>
                  <p className="text-2xs text-center text-[var(--zc-text-3)] mb-3">
                    {listening
                      ? "Listening… tap mic when done"
                      : input.trim()
                        ? "Check the transcript, then tap send"
                        : "Tap mic, say city & dates, then send"}
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => setSpeakReplies(v => !v)}
                      className="w-9 h-9 grid place-items-center rounded-full border border-[var(--zc-border)] text-[var(--zc-text-3)]"
                      aria-label={speakReplies ? `Mute ${AI_NAME} voice` : `Enable ${AI_NAME} voice`}
                      data-testid="ai-chat-voice-mute"
                    >
                      {speakReplies ? <Volume2 size={14} /> : <VolumeX size={14} />}
                    </button>
                    <button
                      type="button"
                      onClick={toggleMic}
                      disabled={sending}
                      className={"w-14 h-14 grid place-items-center rounded-full transition " +
                        (listening ? "zc-btn-primary animate-pulse" : "border-2 border-[var(--zc-accent)] text-[var(--zc-accent)] bg-[var(--zc-accent-soft)]")}
                      data-testid="ai-chat-mic"
                      aria-label={listening ? "Stop listening" : "Start listening"}
                    >
                      {listening ? <MicOff size={22} /> : <Mic size={22} />}
                    </button>
                    <button
                      type="button"
                      onClick={send}
                      disabled={sending || !input.trim()}
                      className="w-9 h-9 grid place-items-center zc-btn-primary rounded-full disabled:opacity-40"
                      aria-label="Send edited transcript"
                      data-testid="ai-chat-voice-send"
                    >
                      <Send size={14} />
                    </button>
                  </div>
                  {voiceError && (
                    <p className="text-2xs text-center text-[var(--zc-danger,#dc2626)] mt-2">{voiceError}</p>
                  )}
                  {!listening && (
                    <input
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") send(); }}
                      placeholder={input ? `Edit what ${AI_NAME} heard…` : "Transcript appears here after you speak"}
                      className="zc-input w-full h-9 px-3 text-xs mt-3"
                      data-testid="ai-chat-voice-edit"
                      aria-label="Voice transcript"
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
